import { ROLE_LANDING, type DisplayRole } from '@/features/auth/roles';

import type { NavItem } from '@/components/app-shell/nav-items';

/**
 * Single source of truth mapping each stable nav id (NavItem['id']) to its URL
 * path, and back. SCRUM-616: the router-less AppShell keyed navigation on the
 * nav id held in component-local state; TanStack Router keys it on the URL, so
 * every nav id needs a canonical path.
 *
 * The nav ids come from NAV_ITEMS + FOOTER_SETTINGS_ITEM (nav-items.ts). All
 * paths are absolute and live under the authed layout route.
 */
export const NAV_ID_TO_PATH = {
  dashboard: '/dashboard',
  'teller-dashboard': '/teller-dashboard',
  'customer-dashboard': '/customer-dashboard',
  'user-activity': '/user-activity',
  campaigns: '/campaigns',
  vault: '/vault',
  'ask-bankiq': '/ask-bankiq',
  referrals: '/referrals',
  ingestion: '/ingestion',
  settings: '/settings',
} as const satisfies Record<string, string>;

export type NavId = keyof typeof NAV_ID_TO_PATH;
export type AppPath = (typeof NAV_ID_TO_PATH)[NavId];

/** Reverse lookup: URL path -> nav id (for driving SideNav active state). */
export const PATH_TO_NAV_ID: Readonly<Record<string, NavId>> = Object.fromEntries(
  Object.entries(NAV_ID_TO_PATH).map(([id, path]) => [path, id]),
) as Record<string, NavId>;

/**
 * The landing PATH for a role (from ROLE_LANDING's nav id). Returns null for
 * roles with no landing (Unassigned) — callers render the pending-access state
 * instead of navigating. Bank roles -> /dashboard, Platform Admin ->
 * /customer-dashboard.
 */
export function roleLandingPath(role: DisplayRole): AppPath | null {
  const navId = ROLE_LANDING[role];
  if (navId === null) return null;
  // Index without asserting the key type so noUncheckedIndexedAccess keeps the
  // `| undefined`, making the dashboard fallback a genuine (lint-clean) guard.
  const path: AppPath | undefined = (NAV_ID_TO_PATH as Record<string, AppPath>)[navId];
  return path ?? NAV_ID_TO_PATH.dashboard;
}

/**
 * Whether a role may reach a given nav id, mirroring the SideNav visibility
 * rules (NAV_ITEMS[].roles + SETTINGS_ROLES). This is CLIENT-SIDE PARITY with
 * today's nav hiding — NOT server-side authorization (that is SCRUM-103). Used
 * by route beforeLoad guards to redirect a hand-typed URL a user can't see back
 * to their landing, matching the pre-router behavior where such pages were
 * simply not in the nav.
 */
export function canRoleAccessNavId(
  role: DisplayRole,
  navId: NavId,
  navItems: readonly NavItem[],
  settingsRoles: readonly DisplayRole[],
): boolean {
  if (navId === 'settings') return settingsRoles.includes(role);
  const item = navItems.find((i) => i.id === navId);
  // If an id is not in NAV_ITEMS (shouldn't happen), fail closed to landing.
  return item ? item.roles.includes(role) : false;
}
